import { action, makeObservable, observable, runInAction, observableShallow } from 'mobx';

import {
  AuditEventsQuery, CustodyAuditExportFormat, CustodyAuditOrder,
  CustodyAuditResourceType, CustodyAuditScopeInput, CustodyAuditTimelinePreset,
} from '@clipr/lib';

import { StoreNode } from '../../store';
import type { Store } from '../../store/store';

type AuditEvent = AuditEventsQuery['auditEvents']['events'][number];

const PAGE_SIZE = 50;
const AGENCY_PAGE_SIZE = 200;
const DATE_RANGE = /^(\d{4}-\d{2}-\d{2})\s*(?:\.\.|\bto\b)\s*(\d{4}-\d{2}-\d{2})$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const toDateTimeLocal = (date: Date): string => new Date(
  date.getTime() - date.getTimezoneOffset() * 60 * 1000,
).toISOString().slice(0, 16);

export class CustodyAuditPageState extends StoreNode {
  private requestSeq = 0;
  private dateRangeExplicit = false;

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      preset: observable,
      from: observable,
      to: observable,
      search: observable,
      agencyId: observable,
      agencyName: observable,
      actorUserId: observable,
      actorEmail: observable,
      actions: observable,
      resourceType: observable,
      resourceId: observable,
      order: observable,
      events: observableShallow,
      endCursor: observable,
      hasNextPage: observable,
      isLoading: observable,
      isExporting: observable,
      error: observable,
      downloadUrl: observable,
      setPreset: action,
      setFrom: action,
      setTo: action,
      setSearch: action,
      setAgencyId: action,
      setAgencyName: action,
      setActorUserId: action,
      setActorEmail: action,
      setActor: action,
      setActions: action,
      setResourceType: action,
      setResourceId: action,
      setOrder: action,
      open: action,
    });
  }

  preset: CustodyAuditTimelinePreset = CustodyAuditTimelinePreset.Global;
  from = toDateTimeLocal(new Date(Date.now() - 24 * 60 * 60 * 1000));
  to = toDateTimeLocal(new Date());
  search = '';
  agencyId = '';
  agencyName = '';
  actorUserId = '';
  actorEmail = '';
  actions = '';
  resourceType: CustodyAuditResourceType = CustodyAuditResourceType.Job;
  resourceId = '';
  order: CustodyAuditOrder = CustodyAuditOrder.Asc;
  events: AuditEvent[] = [];
  endCursor: string | undefined;
  hasNextPage = false;
  isLoading = false;
  isExporting = false;
  error: string | null = null;
  downloadUrl: string | null = null;

  setPreset(value: CustodyAuditTimelinePreset) { this.preset = value; }
  setFrom(value: string) { this.from = value; this.dateRangeExplicit = true; }
  setTo(value: string) { this.to = value; this.dateRangeExplicit = true; }
  setSearch(value: string) {
    this.search = value;
    const range = value.trim().match(DATE_RANGE);
    if (range) {
      this.dateRangeExplicit = true;
      this.from = `${range[1]}T00:00`;
      this.to = `${range[2]}T23:59`;
    }
  }
  setAgencyId(value: string) { this.agencyId = value; }
  setAgencyName(value: string) { this.agencyName = value; }
  setActorUserId(value: string) { this.actorUserId = value; }
  setActorEmail(value: string) { this.actorEmail = value; }
  get actor() { return this.actorEmail || this.actorUserId; }
  setActor(value: string) {
    const actor = value.trim();
    this.actorEmail = EMAIL.test(actor) ? actor : '';
    this.actorUserId = this.actorEmail ? '' : actor;
  }
  setActions(value: string) { this.actions = value; }
  setResourceType(value: CustodyAuditResourceType) { this.resourceType = value; }
  setResourceId(value: string) { this.resourceId = value; }
  setOrder(value: CustodyAuditOrder) { this.order = value; }

  open(search: string): Promise<void> {
    const params = new URLSearchParams(search);
    const now = new Date();
    this.preset = CustodyAuditTimelinePreset.Global;
    this.dateRangeExplicit = false;
    this.from = toDateTimeLocal(new Date(now.getTime() - 24 * 60 * 60 * 1000));
    this.to = toDateTimeLocal(now);
    this.agencyId = params.get('agencyId') ?? '';
    this.agencyName = '';
    this.actorUserId = '';
    this.actorEmail = '';
    this.actions = '';
    this.resourceId = params.get('resourceId') ?? '';
    const resourceType = params.get('resourceType');
    const isResourceTypeValid = resourceType === null
      || Object.values(CustodyAuditResourceType).includes(resourceType as CustodyAuditResourceType);
    this.resourceType = resourceType && isResourceTypeValid
      ? resourceType as CustodyAuditResourceType
      : CustodyAuditResourceType.Job;
    this.setSearch(params.get('query') ?? '');
    this.order = CustodyAuditOrder.Asc;
    if (this.resourceId) this.preset = CustodyAuditTimelinePreset.File;
    else if (this.agencyId) this.preset = CustodyAuditTimelinePreset.Agency;
    this.events = [];
    this.endCursor = undefined;
    this.hasNextPage = false;
    this.error = null;
    this.downloadUrl = null;
    if (!isResourceTypeValid) {
      this.requestSeq += 1;
      this.isLoading = false;
      this.error = 'Invalid custody audit resource type.';
      return Promise.resolve();
    }
    return this.load();
  }

  async probeAccess() {
    const now = new Date();
    // The server records the refusal; its error is expected and not shown.
    await this.store.apiService.auditEvents({
      scope: {
        preset: CustodyAuditTimelinePreset.Global,
        from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
        to: now.toISOString(),
        order: CustodyAuditOrder.Asc,
      },
      first: 1,
    }).catch(() => undefined);
  }

  async load() { await this.request(undefined, false, ++this.requestSeq); }

  async loadNextPage() {
    if (!this.isLoading && this.hasNextPage && this.endCursor)
      await this.request(this.endCursor, true, this.requestSeq);
  }

  async export(format: CustodyAuditExportFormat) {
    this.isExporting = true;
    this.error = null;
    const [result, error] = await this.store.apiService.exportAuditEvents({
      scope: await this.resolvedScope(), format,
    });
    runInAction(() => {
      this.isExporting = false;
      this.error = error?.message ?? null;
      this.downloadUrl = result?.exportAuditEvents.downloadUrl ?? null;
    });
  }

  private async request(after?: string, append = false, seq = this.requestSeq) {
    this.isLoading = true;
    this.error = null;
    const [result, error] = await this.store.apiService.auditEvents({
      scope: await this.resolvedScope(), first: PAGE_SIZE, ...(after ? { after } : {}),
    });
    runInAction(() => {
      if (seq !== this.requestSeq) return;
      this.isLoading = false;
      this.error = error?.message ?? null;
      if (!result) return;
      this.events = append ? [...this.events, ...result.auditEvents.events] : result.auditEvents.events;
      this.endCursor = result.auditEvents.pageInfo.endCursor;
      this.hasNextPage = result.auditEvents.pageInfo.hasNextPage;
    });
  }

  private async resolvedScope(): Promise<CustodyAuditScopeInput> {
    const scope = this.historyScope(this.scope());
    const term = this.search.trim();
    if (!term) return scope;
    const range = term.match(DATE_RANGE);
    if (range) {
      return { ...scope, to: new Date(`${range[2]}T23:59:59.999`).toISOString() };
    }

    if (EMAIL.test(term)) return { ...scope, actorEmail: term };

    const match = term.match(/^(file|video|library|agency)\s*:\s*(.+)$/i);
    if (match?.[1].toLowerCase() === 'agency') {
      if (scope.agencyId) return scope;
      const agency = await this.resolveAgency(match[2].trim());
      return agency
        ? this.historyScope({ ...scope, preset: CustodyAuditTimelinePreset.Agency, agencyId: agency.orgId })
        : { ...scope, preset: CustodyAuditTimelinePreset.Global, agencyName: match[2].trim() };
    }

    if (match) {
      const type = match[1].toLowerCase() === 'library'
        ? CustodyAuditResourceType.Team
        : CustodyAuditResourceType.Job;
      return this.resolveResource(scope, type, match[2].trim());
    }

    const jobScope = await this.resolveResource(scope, CustodyAuditResourceType.Job, term, false);
    if (jobScope.preset === CustodyAuditTimelinePreset.File) return jobScope;
    const teamScope = await this.resolveResource(scope, CustodyAuditResourceType.Team, term, false);
    if (teamScope.preset === CustodyAuditTimelinePreset.File) return teamScope;
    const agency = await this.resolveAgency(term);
    return agency
      ? this.historyScope({ ...scope, preset: CustodyAuditTimelinePreset.Agency, agencyId: agency.orgId })
      : { ...scope, resourceId: term };
  }

  private async resolveResource(
    scope: CustodyAuditScopeInput,
    type: CustodyAuditResourceType,
    id: string,
    retainTypedResource = true,
  ): Promise<CustodyAuditScopeInput> {
    const resource = { type, id };
    if (scope.agencyId) {
      return this.historyScope({ ...scope, preset: CustodyAuditTimelinePreset.File, resource });
    }
    const entity = type === CustodyAuditResourceType.Job
      ? (await this.store.apiService.getJob({ id }))[0]?.getJob
      : (await this.store.apiService.getTeam({ id }))[0]?.getTeam;
    let agencyId = entity?.organizationId;
    let found = Boolean(entity);
    if (!agencyId) {
      const [probe] = await this.store.apiService.auditEvents({
        scope: {
          preset: CustodyAuditTimelinePreset.Global,
          from: new Date(0).toISOString(),
          to: new Date().toISOString(),
          order: CustodyAuditOrder.Desc,
          resource,
        },
        first: 1,
      });
      agencyId = probe?.auditEvents.events.find(event => event.agencyId)?.agencyId;
      found ||= Boolean(probe?.auditEvents.events.length);
    }
    if (agencyId) {
      return this.historyScope({ ...scope, preset: CustodyAuditTimelinePreset.File, agencyId, resource });
    }
    return retainTypedResource || found
      ? this.historyScope({ ...scope, preset: CustodyAuditTimelinePreset.File, resource }) : scope;
  }

  private historyScope(scope: CustodyAuditScopeInput): CustodyAuditScopeInput {
    if (this.dateRangeExplicit || scope.preset === CustodyAuditTimelinePreset.Global) return scope;
    runInAction(() => { this.from = toDateTimeLocal(new Date(0)); });
    return { ...scope, from: new Date(0).toISOString() };
  }

  private async resolveAgency(value: string): Promise<{ orgId: string } | undefined> {
    const [result] = await this.store.apiService.adminListAgencies({
      first: AGENCY_PAGE_SIZE, filter: { _search: value },
    });
    return result?.adminListAgencies.edges
      .map(edge => edge.node)
      .find(agency => agency.orgId === value || agency.agencyName.toLowerCase() === value.toLowerCase());
  }

  private scope(): CustodyAuditScopeInput {
    const iso = (value: string) => new Date(value).toISOString();
    const actions = this.actions.split(',').map(action => action.trim()).filter(Boolean);
    return {
      preset: this.preset, from: iso(this.from), to: iso(this.to), order: this.order,
      ...(this.agencyId.trim() ? { agencyId: this.agencyId.trim() } : {}),
      ...(this.agencyName.trim() ? { agencyName: this.agencyName.trim() } : {}),
      ...(this.actorUserId.trim() ? { actorUserId: this.actorUserId.trim() } : {}),
      ...(this.actorEmail.trim() ? { actorEmail: this.actorEmail.trim() } : {}),
      ...(actions.length ? { actions } : {}),
      ...(this.resourceId.trim() ? { resource: { type: this.resourceType, id: this.resourceId.trim() } } : {}),
    };
  }
}
